import { AfterContentInit, ChangeDetectionStrategy, Component, ContentChildren, EventEmitter, forwardRef, Input, OnDestroy, Output, QueryList } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import { ApxError } from '../models/error.model';
import { ApxControlDirective } from '../form/control.directive';
import { ApxRadioButtonComponent } from './radio-button/radio-button.component';
import { ComponentSubscription } from '../utils/decorators/component-subscription';
import { ApxControlChange } from '../models/change.model';

@Component({
    selector: 'apx-radio-group',
    standalone: true,
    imports: [CommonModule],
    templateUrl: './radio-group.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: ApxControlDirective,
            useExisting: forwardRef(() => ApxRadioGroupComponent)
        },
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => ApxRadioGroupComponent),
            multi: true
        }
    ]
})
export class ApxRadioGroupComponent extends ApxControlDirective implements ControlValueAccessor, AfterContentInit, OnDestroy {
    @ComponentSubscription
    subs: Subscription[] = [];
    @ContentChildren(ApxRadioButtonComponent)
    radios!: QueryList<ApxRadioButtonComponent>;

    @Input() label!: string;
    @Input() override set value(value: string) {
        /* set value will be used if there is a [value]="" attached to the `<input type="text">`
    but we can re-use the writeValue that we have defined */
        this.writeValue(value);
    }
    @Input() set layout(layout: 'standard' | 'vertical') {
        this._layout = layout;
        this.updateRadioLayout(layout);
    }
    get layout() {
        return this._layout;
    }
    @Output() readonly valueChanged = new EventEmitter<ApxControlChange>();

    private _layout: 'standard' | 'vertical' = 'standard';
    private _radioValue = '';
    private _disabled!: boolean;

    private set radioValue(value: string) {
        if (this.changed) {
            this.changed(value);
        }
        this._radioValue = value;
    }

    private get radioValue() {
        return this._radioValue;
    }

    private get disabled() {
        return this._disabled;
    }

    private set disabled(disabled: boolean) {
        this._disabled = disabled;
    }

    public changed!: (value: unknown) => void;
    public touched!: () => void;

    writeValue(value: string): void {
        this.radioValue = value;
        this.updateRadioValues(value);
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    registerOnChange(fn: any): void {
        this.changed = fn;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    registerOnTouched(fn: any): void {
        this.touched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
        this.updateRadioDisabledState(isDisabled);
    }

    ngAfterContentInit(): void {
        if (!this.radios?.length) {
            throw new ApxError('No ApxRadioButtonComponent instances found. Be sure to use ApxRadioButtonComponent inside a ApxRadioGroupComponent');
        }
        this.initializeRadioSubscriptions();
        if (this.disabled !== null && this.disabled !== undefined) {
            this.updateRadioDisabledState(this.disabled);
        }
        this.updateRadioValues(this.radioValue);
        this.updateRadioLayout(this.layout);
    }

    // eslint-disable-next-line @angular-eslint/no-empty-lifecycle-method, @typescript-eslint/no-empty-function
    ngOnDestroy(): void {}

    /**
     * Set properties and listeners for output event streams on ApxRadioButtonComponent instances
     */
    private initializeRadioSubscriptions() {
        this.radios.forEach(radio => {
            this.subs.push(
                radio.changed.subscribe(({ value }) => {
                    this.radioValue = value;
                    this.valueChanged.emit({ value });
                    this.updateRadioValues(this.radioValue);
                })
            );
        });
    }

    /**
     * Update the individual ApxRadioButtonComponent instances and set checked where applicable
     * @param value
     */
    private updateRadioValues(value: string): void {
        if (!this.radios?.length) {
            return;
        }

        const matchingControl = this.radios.find(radio => radio.value === value);
        this.radios.forEach((radio, idx) => {
            radio.checked = false;
            radio.isFirst = idx === 0;
            radio.isLast = idx === this.radios.length - 1;
            radio.cdr.markForCheck();
        });

        if (matchingControl) {
            matchingControl.checked = true;
        }
    }

    /**
     * Cascade the global 'disabled' state to disable all ApxRadioButtonComponent instances
     * @param disabled
     */
    private updateRadioDisabledState(disabled: boolean) {
        this.radios?.forEach(radio => {
            radio.disabled = disabled;
        });
    }

    /**
     * Cascade the global 'layout' state to disable all ApxRadioButtonComponent instances
     * @param layout
     */
    private updateRadioLayout(layout: 'standard' | 'vertical'): void {
        this.radios?.forEach(radio => {
            radio.isVertical = layout === 'vertical';
            radio.cdr.markForCheck();
        });
    }
}
